import { Button, Input, Modal, notify } from '@affine/component';
import { getAFFiNEWorkspaceSchema } from '@affine/core/modules/workspace/global-schema';
import { useI18n } from '@affine/i18n';
import {
  docLinkBaseURLMiddleware,
  embedSyncedDocMiddleware,
  MarkdownAdapterFactoryIdentifier,
  titleMiddleware,
} from '@blocksuite/affine/shared/adapters';
import { Transformer } from '@blocksuite/affine/store';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { FetchService } from '../../../modules/cloud';
import { DocsService } from '../../../modules/doc';
import { WorkspaceService } from '../../../modules/workspace';
import { useDevtoPublish } from './use-devto-publish';
import * as styles from './publish-dialog.css';

// Helper to escape special regex characters
function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface DevtoPublishDialogProps {
  docId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DevtoPublishDialog = ({
  docId,
  open,
  onOpenChange,
}: DevtoPublishDialogProps) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const workspaceService = useService(WorkspaceService);
  const fetchService = useService(FetchService);
  const doc = useLiveData(docsService.list.doc$(docId));
  const docTitle = useLiveData(doc?.title$) || 'Untitled';

  const { isConnected, publishArticle, loading, error } = useDevtoPublish();

  const [title, setTitle] = useState(docTitle);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [series, setSeries] = useState('');
  const [canonicalUrl, setCanonicalUrl] = useState('');
  const [publishAsDraft, setPublishAsDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);

  // Check if Dev.to is configured when dialog opens
  useEffect(() => {
    if (open) {
      setTitle(docTitle);
      setDescription('');
      setTags('');
      setSeries('');
      setCanonicalUrl('');
      setPublishAsDraft(false);
      // Check if API key is configured
      setConfigured(isConnected());
    }
  }, [open, docTitle, isConnected]);

  const getMarkdownContent = useCallback(async (): Promise<string> => {
    // Use docsService.open() which properly loads the document
    const { doc: openedDoc, release } = docsService.open(docId);

    try {
      // blockSuiteDoc is already a Store
      const store = openedDoc.blockSuiteDoc;

      if (!store) {
        throw new Error('Document not found');
      }

      // Wait for document to be fully synced
      await workspaceService.workspace.engine.doc.waitForDocLoaded(docId);

      if (!store.root) {
        throw new Error('Document has no content');
      }

      const transformer = new Transformer({
        schema: getAFFiNEWorkspaceSchema(),
        blobCRUD: store.workspace.blobSync,
        docCRUD: {
          create: (id: string) =>
            store.workspace.createDoc(id).getStore({ id }),
          get: (id: string) =>
            store.workspace.getDoc(id)?.getStore({ id }) ?? null,
          delete: (id: string) => store.workspace.removeDoc(id),
        },
        middlewares: [
          docLinkBaseURLMiddleware(store.workspace.id),
          titleMiddleware(store.workspace.meta.docMetas),
          embedSyncedDocMiddleware('content'),
        ],
      });

      const adapterFactory = store.provider.get(MarkdownAdapterFactoryIdentifier);
      const adapter = adapterFactory.get(transformer);
      const result = await adapter.fromDoc(store);

      return result?.file || '';
    } finally {
      release();
    }
  }, [docId, docsService, workspaceService]);

  // Extract blob references from markdown content
  // Returns array of { key, fullRef } where key is the blob hash
  const extractBlobRefs = useCallback(
    (markdown: string): Array<{ key: string; fullRef: string }> => {
      const blobRefs: Array<{ key: string; fullRef: string }> = [];
      const seen = new Set<string>();

      // Pattern for image references: ![...](assets/hashKey.extension)
      const imagePattern = /!\[[^\]]*\]\((assets\/[^)]+)\)/g;
      let match;

      while ((match = imagePattern.exec(markdown)) !== null) {
        const fullRef = match[1]; // e.g., "assets/abc123=.png"
        if (
          !fullRef.startsWith('http://') &&
          !fullRef.startsWith('https://')
        ) {
          // Extract the key (remove assets/ prefix and extension)
          const withoutPrefix = fullRef.replace(/^assets\//, '');
          const key = withoutPrefix.replace(/\.[a-zA-Z]+$/, '');

          if (key && !seen.has(key)) {
            seen.add(key);
            blobRefs.push({ key, fullRef });
          }
        }
      }

      return blobRefs;
    },
    []
  );

  // Request signed URLs from backend and replace blob refs in markdown
  const transformBlobRefsToSignedUrls = useCallback(
    async (markdown: string): Promise<string> => {
      const blobRefs = extractBlobRefs(markdown);
      if (blobRefs.length === 0) {
        return markdown;
      }

      const workspaceId = workspaceService.workspace.id;
      const blobKeys = blobRefs.map(ref => ref.key);

      console.log(`Requesting signed URLs for ${blobKeys.length} blobs`);

      try {
        // Request signed URLs from backend
        const response = await fetchService.fetch('/api/devto/sign-blob-urls', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            workspaceId,
            blobKeys,
            expiresInMinutes: 60, // 1 hour should be enough for dev.to to fetch
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to get signed URLs: ${response.status}`);
        }

        const { signedUrls } = await response.json();

        // Replace blob refs with signed URLs
        let processedMarkdown = markdown;
        for (const ref of blobRefs) {
          const signedUrl = signedUrls[ref.key];
          if (signedUrl) {
            processedMarkdown = processedMarkdown.replace(
              new RegExp(escapeRegex(ref.fullRef), 'g'),
              signedUrl
            );
            console.log(`Transformed ${ref.fullRef} -> signed URL`);
          }
        }

        return processedMarkdown;
      } catch (error) {
        console.error('Failed to get signed URLs:', error);
        // Return original markdown if signing fails
        return markdown;
      }
    },
    [extractBlobRefs, fetchService, workspaceService]
  );

  const handlePublish = useCallback(async () => {
    if (!title.trim()) {
      notify.error({
        title: 'Please enter a title',
      });
      return;
    }

    setPublishing(true);
    try {
      const rawMarkdown = await getMarkdownContent();

      if (!rawMarkdown.trim()) {
        notify.error({
          title: 'Document is empty',
          message: 'Cannot publish an empty document',
        });
        return;
      }

      // Transform blob refs to signed URLs for external access
      const markdown = await transformBlobRefsToSignedUrls(rawMarkdown);
      console.log('Publishing markdown with signed image URLs');

      const tagList = tags
        .split(',')
        .map(tag => tag.trim().toLowerCase().replace(/\s+/g, ''))
        .filter(tag => tag.length > 0)
        .slice(0, 4); // Dev.to allows max 4 tags

      const result = await publishArticle(title.trim(), markdown, {
        description: description.trim() || undefined,
        tags: tagList.length > 0 ? tagList : undefined,
        series: series.trim() || undefined,
        canonicalUrl: canonicalUrl.trim() || undefined,
        published: !publishAsDraft,
      });

      notify.success({
        title: publishAsDraft ? 'Draft saved!' : 'Published successfully!',
        message: publishAsDraft
          ? 'Your article has been saved as a draft on Dev.to'
          : 'Your article has been published to Dev.to',
        action: {
          label: 'View',
          onClick: () => {
            window.open(result.articleUrl, '_blank');
          },
        },
      });

      onOpenChange(false);
    } catch (err) {
      console.error('Failed to publish:', err);
      notify.error({
        title: 'Failed to publish',
        message: err instanceof Error ? err.message : 'Please try again later',
      });
    } finally {
      setPublishing(false);
    }
  }, [
    title,
    description,
    tags,
    series,
    canonicalUrl,
    publishAsDraft,
    getMarkdownContent,
    transformBlobRefsToSignedUrls,
    publishArticle,
    onOpenChange,
  ]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const isLoading = loading || publishing;

  if (configured === false) {
    return (
      <Modal
        open={open}
        title="Publish to Dev.to"
        onOpenChange={onOpenChange}
        width={480}
      >
        <div className={styles.content}>
          <div className={styles.error}>
            Dev.to is not connected. Please connect your Dev.to account in
            Settings &gt; Workspace &gt; Integrations.
          </div>
          <div className={styles.hint}>
            You'll need to generate an API key at{' '}
            <a
              href="https://dev.to/settings/extensions"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              dev.to/settings/extensions
            </a>
          </div>
        </div>
        <div className={styles.footer}>
          <Button size="large" onClick={handleCancel}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Publish to Dev.to"
      onOpenChange={onOpenChange}
      width={480}
    >
      <div className={styles.content}>
        <div className={styles.field}>
          <label className={styles.label}>Title</label>
          <Input
            value={title}
            onChange={setTitle}
            placeholder="Enter a title for your article"
            disabled={isLoading}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Description (optional)</label>
          <Input
            value={description}
            onChange={setDescription}
            placeholder="Brief description for SEO"
            disabled={isLoading}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Tags (optional)</label>
          <Input
            value={tags}
            onChange={setTags}
            placeholder="javascript, webdev, tutorial"
            disabled={isLoading}
          />
          <div className={styles.hint}>
            Up to 4 tags, separated by commas. No spaces or special characters.
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Series (optional)</label>
          <Input
            value={series}
            onChange={setSeries}
            placeholder="My Tutorial Series"
            disabled={isLoading}
          />
          <div className={styles.hint}>
            Group articles together in a series
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Canonical URL (optional)</label>
          <Input
            value={canonicalUrl}
            onChange={setCanonicalUrl}
            placeholder="https://yourblog.com/original-article"
            disabled={isLoading}
          />
          <div className={styles.hint}>
            If this was originally published elsewhere, enter the original URL
          </div>
        </div>

        <div className={styles.field}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={publishAsDraft}
              onChange={e => setPublishAsDraft(e.target.checked)}
              disabled={isLoading}
            />
            <span className={styles.label} style={{ marginBottom: 0 }}>
              Save as draft (don't publish yet)
            </span>
          </label>
        </div>

        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.footer}>
        <Button size="large" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          size="large"
          variant="primary"
          onClick={handlePublish}
          disabled={isLoading || !title.trim()}
        >
          {publishing
            ? 'Publishing...'
            : publishAsDraft
              ? 'Save Draft'
              : 'Publish'}
        </Button>
      </div>
    </Modal>
  );
};
